import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddCommasToAmount } from '../../../helper/Utils';
import DataTable from 'react-data-table-component';
import { ADD_CHALLAN_FAIL, ADD_CHALLAN_START } from '../../../RTK/Slices/ChallanSlice';
import { addTDSChallan, get_invoice_details_by_invoice_id } from '../../../helper/Api';
import { extractDocumentServePath, openSecureDocumentOrToast } from '../../../helper/secureDocuments';
import { toast } from 'react-toastify';
import { Badge } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { validateUploadFileList } from '../../../helper/uploadValidation';
import { TextField } from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import Spinner from '../../../components/Tools/spinner';
import CustomBreadcrumbs from '../../../components/Tools/Breadcumbs';
import './AddTDSITChallan.css';

const MultiLineHeader = ({ lines, align = 'center' }) => (
    <div className={`tdsit-col-header tdsit-col-header--${align}`}>
        {lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ))}
    </div>
);

const CenterCell = ({ children }) => (
    <div className="tdsit-cell-center">{children}</div>
);

const actionIconBtnStyle = {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    background: 'transparent',
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getPaymentTypeLabel = (paymentType) => {
    if (paymentType === '1') return 'FULL PAYMENT';
    if (paymentType === '2') return 'PART PAYMENT';
    return '';
};

const isDocumentAvailable = (value) => {
    if (value == null) return false;
    const text = String(value).trim();
    if (!text || text === '0' || text.toLowerCase() === 'null') return false;
    if (/https?:\/\//i.test(text)) return false;
    return true;
};

const DOCUMENT_OPEN_ORDER = [
    { type: 'invoice', field: 'invoice', label: 'Invoice' },
    { type: 'voucher', field: 'voucher', label: 'Voucher' },
    { type: 'sanction_order', field: 'sanction_order', label: 'Sanction Order' },
];

const invoiceRowId = (row) => {
    const id = row?.invoice_id || row?.id;
    if (id == null || String(id).trim() === '') return '';
    return String(id).trim();
};

const documentRecordId = (details, type) => {
    if (type === 'invoice') return details?.invoice_id || details?.id || '';
    if (type === 'voucher') return details?.voucher_id || '';
    if (type === 'sanction_order') return details?.sanction_order_id || details?.sanction_id || '';
    return '';
};

const resolveDocumentTarget = (details, row) => {
    const chosen = DOCUMENT_OPEN_ORDER.find((doc) => isDocumentAvailable(details?.[doc.field]));
    if (!chosen) return null;

    const stored = details[chosen.field];
    const recordId = documentRecordId(details, chosen.type);
    const servePath = extractDocumentServePath(stored);
    const ref = servePath || (recordId ? `documents/serve/${chosen.type}/${recordId}/${chosen.field}` : '');
    if (!ref) return null;

    return {
        ref,
        label: chosen.label,
        filename: `${chosen.field}_${recordId || row?.invoice_no || 'document'}`,
    };
};

const AddTDSITChallan = () => {
    const [loading, setLoading] = useState(false);
    const { state } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [data] = useState(state?.data ? state.data : []);
    const [docTargets, setDocTargets] = useState({});

    const [formData, setFormData] = useState({
        id: '',
        invoices: [],
        challan_no: '',
        challan_date: '',
        cheque_no: '',
        cheque_date: '',
        tds_challan_value: '',
        challan_url: '',
        attachments: [],
    });

    const sectionLabelForRow = (row) => {
        const label = row?.tds_section || row?.tds_section_name || row?.section || '';
        if (label) return label;
        const id = row?.tds_section_id ?? row?.tds_it_section;
        if (id !== null && id !== undefined && String(id).trim() !== '') {
            return `Section ${id}`;
        }
        return '';
    };

    const sectionIdForRow = (row) => {
        const id = row?.tds_section_id ?? row?.tds_it_section;
        if (id === null || id === undefined || String(id).trim() === '') return '';
        return String(id);
    };

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 400,
                fontSize: '14px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    useEffect(() => {
        let cancelled = false;
        const rows = data.filter((row) => invoiceRowId(row));
        if (!rows.length) return undefined;

        const loadTargets = async () => {
            const next = {};
            await Promise.all(rows.map(async (row) => {
                const listId = invoiceRowId(row);
                try {
                    const { data: response } = await get_invoice_details_by_invoice_id(listId);
                    const details = response?.status ? response.details : null;
                    const target = resolveDocumentTarget(details, row);
                    if (target) next[listId] = target;
                } catch (error) {
                    next[listId] = null;
                }
            }));
            if (!cancelled) setDocTargets(next);
        };

        loadTargets();
        return () => {
            cancelled = true;
        };
    }, [data]);

    const openInvoiceDocument = (row) => {
        const target = docTargets[invoiceRowId(row)];
        if (!target?.ref) return;
        openSecureDocumentOrToast(target.ref, { fallbackFilename: target.filename });
    };

    const columns = [
        {
            name: <MultiLineHeader lines={['Invoice No', 'Invoice Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return row.invoice_no;
                const invoiceDate = row.invoice_date
                    ? moment(row.invoice_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.invoice_no || ''}</b></div>
                        {invoiceDate && <div className="text-muted fs-12px">{invoiceDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '130px',
            grow: 1.2,
        },
        {
            name: <MultiLineHeader lines={['Beneficiary', 'Details']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return (
                    <div>
                        <div>{row.company_name || ''}</div>
                        <div className="text-muted fs-12px">
                            <b>Pan No.:</b> {row.pan_no || ''}
                        </div>
                    </div>
                );
            },
            wrap: true,
            minWidth: '170px',
            grow: 2,
        },
        {
            name: <MultiLineHeader lines={['Payment', 'Type']} align="center" />,
            selector: (row) => (row.isFooter ? '' : getPaymentTypeLabel(row.payment_type)),
            wrap: true,
            minWidth: '110px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Voucher No', 'Voucher Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                const voucherDate = row.voucher_date
                    ? moment(row.voucher_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.voucher_no || ''}</b></div>
                        {voucherDate && <div className="text-muted fs-12px">{voucherDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '150px',
            grow: 1.5,
        },
        {
            name: <MultiLineHeader lines={['Transaction', 'Date']} align="center" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return row.transaction_date
                    ? moment(row.transaction_date, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']).format('DD-MM-YYYY')
                    : '';
            },
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Taxable', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.taxable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Invoice', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.payable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Sanctioned', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.sanction_amount)),
            minWidth: '115px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['TDS IT', 'Amount']} align="center" />,
            selector: (row) => (
                <CenterCell>
                    {row.isFooter ? row.tds_it_amount : formatAmount(row.tds_it_amount)}
                </CenterCell>
            ),
            minWidth: '110px',
            grow: 1,
            center: true,
        },
        {
            name: 'Section',
            selector: (row) => {
                if (row.isFooter) return '';
                const sectionLabel = sectionLabelForRow(row);
                return (
                    <CenterCell>
                        {sectionLabel ? (
                            <Badge bg="info" style={{ fontSize: 14, fontWeight: 600 }}>
                                {sectionLabel}
                            </Badge>
                        ) : (
                            <span className="text-muted">-</span>
                        )}
                    </CenterCell>
                );
            },
            minWidth: '130px',
            grow: 1,
            center: true,
        },
        {
            name: 'Actions',
            selector: (row) => {
                if (row.isFooter) return null;
                const target = docTargets[invoiceRowId(row)];
                if (!target) return null;
                return (
                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '6px', width: '100%' }}>
                        <button
                            type="button"
                            title={`View ${target.label}`}
                            onClick={() => openInvoiceDocument(row)}
                            style={{
                                ...actionIconBtnStyle,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                cursor: 'pointer',
                            }}
                        >
                            <AiOutlineVerticalAlignBottom size={16} />
                        </button>
                    </div>
                );
            },
            width: '100px',
            minWidth: '100px',
            grow: 0,
            center: true,
        },
    ];

    const calculateTotalAmount = (propertyName) => {
        let total = 0;
        data.forEach((row) => {
            const depositAmount = Number(parseFloat(row[propertyName]));
            if (!Number.isNaN(depositAmount)) {
                total += depositAmount;
            }
        });
        return total.toFixed(2);
    };

    const totalInvoiceAmount = calculateTotalAmount('tds_it_amount');

    const footerRow = {
        isFooter: true,
        invoice_no: <b>Total</b>,
        tds_it_amount: <b>{AddCommasToAmount(totalInvoiceAmount)}</b>,
    };

    const updatedData = [...data, footerRow];

    const handleAttachmentChange = (event) => {
        const files = event.target.files;
        const validation = validateUploadFileList(files);
        if (!validation.ok) {
            toast.error(validation.message, {
                position: toast.POSITION.TOP_CENTER,
            });
            event.target.value = '';
            return;
        }
        setFormData((prev) => ({
            ...prev,
            attachments: files,
        }));
    };

    const handleChallanFormData = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateChallanForm = () => {
        const errors = [];

        if (!data.length) {
            errors.push('No invoices selected for challan.');
        }
        if (!String(formData.challan_no || '').trim()) {
            errors.push('Challan No is required.');
        }
        if (!formData.challan_date) {
            errors.push('Challan Date is required.');
        }
        if (!String(formData.cheque_no || '').trim()) {
            errors.push('Cheque No is required.');
        }
        if (!formData.cheque_date) {
            errors.push('Cheque Date is required.');
        }
        if (!formData.attachments?.length) {
            errors.push('Upload Challan & Cheque is required.');
        }
        if (!Number(totalInvoiceAmount) || Number(totalInvoiceAmount) <= 0) {
            errors.push('Total TDS Challan Value must be greater than zero.');
        }

        return errors;
    };

    const handleSubmit = async () => {
        const validationErrors = validateChallanForm();
        if (validationErrors.length) {
            toast.error(validationErrors[0], {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setLoading(true);
            const invoices = data.map((el) => el.id);
            const post_data = new FormData();

            for (let i = 0; i < formData.attachments.length; i += 1) {
                post_data.append('attachment[]', formData.attachments[i]);
            }

            post_data.append('invoices', invoices.join(','));
            data.forEach((row, index) => {
                post_data.append(`invoice_sections[${index}][invoice_id]`, row.id);
                post_data.append(
                    `invoice_sections[${index}][tds_section_id]`,
                    sectionIdForRow(row)
                );
            });
            post_data.append('challan_no', String(formData.challan_no).trim());
            post_data.append('challan_date', formData.challan_date);
            post_data.append('cheque_no', String(formData.cheque_no).trim());
            post_data.append('cheque_date', formData.cheque_date);
            post_data.append('tds_challan_value', totalInvoiceAmount);

            dispatch(ADD_CHALLAN_START());
            const response = await addTDSChallan(post_data);
            const addChallanResponseData = response.data;

            if (addChallanResponseData.status) {
                toast.success(addChallanResponseData.message || 'Challan added successfully', {
                    position: toast.POSITION.TOP_CENTER,
                });
                navigate('/tds-it');
            } else {
                toast.error(addChallanResponseData.message, {
                    position: toast.POSITION.TOP_CENTER,
                });
                dispatch(ADD_CHALLAN_FAIL(addChallanResponseData.message));
            }
        } catch (error) {
            dispatch(ADD_CHALLAN_FAIL('error getting while updating challan'));
            toast.error(error?.response?.data?.message || 'Failed to add challan', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const linksArray = [
        {
            href: '/tds-it',
            label: 'TDS-IT',
        },
    ];

    return (
        <div className="p-3 add-tdsit-challan-page">
            {loading && <Spinner />}

            <div className="row mb-3">
                <div className="col-md-6">
                    <h4 style={{ color: '#28326f', margin: 0 }}>Add Challan (TDS-IT)</h4>
                    <CustomBreadcrumbs linksArray={linksArray} title="Add Challan (TDS-IT)" />
                </div>
                <div className="col-md-6">
                    <div className="d-flex gap-2" style={{ width: 'auto', float: 'right' }}>
                        <button type="button" className="submit_button" onClick={handleSubmit} disabled={loading}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>

            <div className="card p-3">
                <div className="add-tdsit-challan-datatable-root">
                    <DataTable
                        customStyles={customStyles}
                        className="dataTables_wrapper"
                        columns={columns}
                        data={updatedData}
                        fixedHeader
                        fixedHeaderScrollHeight="320px"
                        conditionalRowStyles={[
                            {
                                when: (row) => row.isFooter,
                                style: {
                                    backgroundColor: '#f8f9fc',
                                    fontWeight: 'bold',
                                },
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="card p-3 mt-3">
                <div className="row">
                    <div className="col-md-4 mb-3">
                        <TextField
                            fullWidth
                            id="challan_no"
                            label="Challan No"
                            variant="outlined"
                            type="text"
                            name="challan_no"
                            value={formData.challan_no || ''}
                            onChange={handleChallanFormData}
                            placeholder="Enter Challan No"
                            required
                        />
                    </div>

                    <div className="col-md-4 mb-3">
                        <TextField
                            fullWidth
                            id="challan_date"
                            label="Challan Date"
                            variant="outlined"
                            type="date"
                            name="challan_date"
                            value={formData.challan_date || ''}
                            onChange={handleChallanFormData}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </div>

                    <div className="col-md-4 mb-3">
                        <TextField
                            fullWidth
                            id="tds_challan_value"
                            label="Challan Value (Auto calculated)"
                            variant="outlined"
                            type="text"
                            name="tds_challan_value"
                            value={formatAmount(totalInvoiceAmount)}
                            InputProps={{ readOnly: true }}
                            disabled
                            required
                        />
                    </div>

                    <div className="col-md-4 mb-3">
                        <TextField
                            fullWidth
                            id="cheque_no"
                            label="Cheque No"
                            variant="outlined"
                            type="text"
                            name="cheque_no"
                            value={formData.cheque_no || ''}
                            onChange={handleChallanFormData}
                            placeholder="Enter Cheque No"
                            required
                        />
                    </div>

                    <div className="col-md-4 mb-3">
                        <TextField
                            fullWidth
                            id="cheque_date"
                            label="Cheque Date"
                            variant="outlined"
                            type="date"
                            name="cheque_date"
                            value={formData.cheque_date || ''}
                            onChange={handleChallanFormData}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </div>

                    <div className="col-md-4 mb-3">
                        <TextField
                            fullWidth
                            id="attachments"
                            label="Upload Challan & Cheque"
                            type="file"
                            name="attachments"
                            onChange={handleAttachmentChange}
                            inputProps={{
                                accept: '.pdf,.jpeg,.jpg,.png',
                            }}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddTDSITChallan;
